import { MouseEvent, KeyboardEvent } from 'react';
import { trackEvent, trackEcommerce } from 'dibs-tracking';
import { graphql } from 'react-relay';
import serverVars from 'server-vars';
import { ValueOf } from 'dibs-ts-utils/exports/ValueOf';

import type { actionButtonsTracking_item } from './__generated__/actionButtonsTracking_item.graphql';
export type Item = actionButtonsTracking_item;

type TrackableEvent = KeyboardEvent | MouseEvent | null;

type PdpTrackingVpvParams = {
    purchaseStatus?: string | null;
    priceType?: string | null;
};

function getPdpTrackingVpvParamValue(
    params: unknown,
    key: keyof PdpTrackingVpvParams
): ValueOf<PdpTrackingVpvParams> | null {
    return (params as PdpTrackingVpvParams)?.[key] || null;
}

function getPurchaseStatus(item: Item): string | null | undefined {
    return getPdpTrackingVpvParamValue(item?.pdpTrackingVpvParams, 'purchaseStatus');
}

export function fireTrackEcommerce(item: Item): void {
    const locale = serverVars.get('locale');
    const name = locale === 'en-US' ? item?.title : 'n/a';
    const {
        seller,
        pricing,
        browseUrl,
        serviceId,
        categoryCode,
        isAuctionEnabled,
        relevantAuctionLot,
        pdpTrackingVpvParams,
        relevantAuctionSolution,
        contemporaryTrackingString,
    } = item || {};

    let price = pricing?.totalAmount?.amount;
    let dimension76 = getPdpTrackingVpvParamValue(pdpTrackingVpvParams, 'priceType');

    if (isAuctionEnabled) {
        const { bids, highestBidAmount } = relevantAuctionSolution || {};
        const hasBids = !!bids?.totalResults;

        price = hasBids ? highestBidAmount?.amount : relevantAuctionLot?.buyItNowPrice?.amount;
        dimension76 = `auction ${!hasBids ? 'bin ' : ''}price`;
    }

    trackEcommerce({
        type: 'addToCart',
        products: [
            {
                brand: seller?.serviceId,
                category: browseUrl,
                categoryCode: categoryCode,
                dimension76,
                dimension83: contemporaryTrackingString,
                id: serviceId,
                name,
                price,
            },
        ],
    });
}

export function trackMakeOfferFromEmail({
    item,
    template,
}: {
    item: Item;
    template?: string | null;
}): void {
    trackEvent({
        category: 'make offer click',
        action: `email make offer button - ${(template || '').toLowerCase()}`,
        label: getPurchaseStatus(item),
        isInteractiveEvent: true,
    });
}

export function trackMakeOfferClick(
    item: Item & { hasPrivateOffer?: boolean },
    event: TrackableEvent = null
): void {
    const hasPrivateOffer = item?.hasPrivateOffer;
    const trackObj = {
        category: 'make offer click',
        action: 'pdp make offer button',
        label: getPurchaseStatus(item),
        additional: { currentOfferType: `${hasPrivateOffer ? 'private' : 'regular'} offer` },
    };
    trackEvent(trackObj, event);
    fireTrackEcommerce(item);
}

export function trackNetPriceClick(item: Item): void {
    trackEvent({
        category: 'request net price',
        action: 'request net price clicked',
        label: getPurchaseStatus(item),
    });
}

export function trackPurchaseClick(item: Item, event: TrackableEvent = null): void {
    const { serviceId, isAuctionEnabled, relevantAuctionSolution, lastTransaction } = item || {};
    const { bids, highestBidAmount } = relevantAuctionSolution || {};
    const hasBids = bids?.totalResults;
    const secondChanceOffer = lastTransaction?.secondChanceOffer;

    if (isAuctionEnabled && (hasBids || secondChanceOffer)) {
        // only fire custom event for auctions with bids or 2nd chance offer
        if (secondChanceOffer) {
            trackEvent(
                {
                    category: 'auction',
                    action: 'auction pdp - accept offer entry',
                    label: 'accept offer button click',
                    value: secondChanceOffer?.amountPerItem?.amount || 0,
                    additional: {
                        itemId: serviceId,
                    },
                },
                event
            );
        } else {
            trackEvent(
                {
                    category: 'auction',
                    action: 'auction pdp - complete purchase entry',
                    label: 'complete purchase button clicked',
                    value: highestBidAmount?.amount || 0,
                    additional: {
                        itemId: serviceId,
                    },
                },
                event
            );
        }
    } else {
        trackEvent(
            {
                category: 'purchase click',
                action: 'pdp purchase button',
                label: getPurchaseStatus(item),
            },
            event
        );
    }
    fireTrackEcommerce(item);
}

// eslint-disable-next-line no-unused-expressions
graphql`
    fragment actionButtonsTracking_item on Item {
        serviceId
        pdpTrackingVpvParams(userId: $userId) @include(if: $isClient)
        contemporaryTrackingString
        title
        vertical
        seller {
            serviceId
        }
        pricing {
            totalAmount {
                amount
            }
        }
        categoryCode
        browseUrl
        isAuctionEnabled
        relevantAuctionSolution(page: pdp) @include(if: $fetchAuction) {
            bids {
                totalResults
            }
            highestBidAmount {
                amount
            }
        }
        relevantAuctionLot(page: pdp) @include(if: $fetchAuction) {
            buyItNowPrice {
                amount
            }
        }
        lastTransaction(buyerId: $userId) @include(if: $fetchAuction) {
            secondChanceOffer {
                amountPerItem {
                    amount
                }
            }
        }
    }
`;
